import { cx } from '../../lib/cx';
import type { TagTone } from '@mybusinessmate/ui-tokens';

export type StatusTagVariant = 'soft' | 'solid' | 'outline';
export type StatusTagSize = 'sm' | 'md';

export interface StatusTagProps {
  label: string;
  tone?: TagTone;
  /** Soft = light tint + dark tone text (default). Solid = filled tone with white text. Outline = white with a border (Cancelling, Suspended). */
  variant?: StatusTagVariant;
  /** `md` is the 24px chip used in staff tables (Figma A03.1, A04). */
  size?: StatusTagSize;
  dot?: boolean;
  className?: string;
}

// Full class names must appear literally for Tailwind to generate them (no `bg-tag-${tone}-bg`).
const SOFT: Record<TagTone, string> = {
  blue: 'bg-tag-blue-bg text-tag-blue-text',
  pink: 'bg-tag-pink-bg text-tag-pink-text',
  indigo: 'bg-tag-indigo-bg text-tag-indigo-text',
  amber: 'bg-tag-amber-bg text-tag-amber-text',
  red: 'bg-tag-red-bg text-tag-red-text',
  green: 'bg-tag-green-bg text-tag-green-text',
  grey: 'bg-tag-grey-bg text-tag-grey-text',
};

const SOLID: Record<TagTone, string> = {
  blue: 'bg-tag-blue-solid-bg text-tag-blue-solid-text',
  pink: 'bg-tag-pink-solid-bg text-tag-pink-solid-text',
  indigo: 'bg-tag-indigo-solid-bg text-tag-indigo-solid-text',
  amber: 'bg-tag-amber-solid-bg text-tag-amber-solid-text',
  red: 'bg-tag-red-solid-bg text-tag-red-solid-text',
  green: 'bg-tag-green-solid-bg text-tag-green-solid-text',
  grey: 'bg-tag-grey-solid-bg text-tag-grey-solid-text',
};

/** Figma "Status Tag" (116:805): 7 tones × soft/solid. Tone mapping per status lives in ui-tokens (`status`, `roles`...). */
export function StatusTag({
  label,
  tone = 'blue',
  variant = 'soft',
  size = 'sm',
  dot = false,
  className,
}: StatusTagProps) {
  const solid = variant === 'solid';
  const outline = variant === 'outline';
  return (
    <span
      className={cx(
        'inline-flex items-center justify-center gap-(--mbm-space-1) self-start rounded-full whitespace-nowrap',
        solid || size === 'md'
          ? 'h-(--mbm-size-tag-height) px-(--mbm-space-2-5)'
          : 'h-(--mbm-space-5) px-(--mbm-space-2)',
        solid ? 'text-label-small' : size === 'md' ? 'text-body-xsmall font-semibold' : 'text-label-tag',
        outline ? 'border border-border-default bg-surface-card text-text-primary' : solid ? SOLID[tone] : SOFT[tone],
        className,
      )}
    >
      {dot ? <span aria-hidden className="size-(--mbm-space-1-5) rounded-full bg-current" /> : null}
      {label}
    </span>
  );
}
